
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  noindex?: boolean;
  schema?: object;
}

function setMetaTag(attr: string, key: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setOrRemoveMetaTag(attr: string, key: string, content?: string) {
  if (content) {
    setMetaTag(attr, key, content);
  } else {
    const el = document.querySelector(`meta[${attr}="${key}"]`);
    if (el) el.remove();
  }
}

export function useSEO({ title, description, keywords, canonical, ogType, ogImage, noindex, schema }: SEOProps) {
  const location = useLocation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://onnyjob.cloud';
  const siteName = import.meta.env.VITE_SITE_NAME || '온니잡';
  const defaultOgImage = import.meta.env.VITE_OG_IMAGE || `${siteUrl}/og-image.jpg`;

  useEffect(() => {
    // Title
    document.title = title;

    // Basic meta
    setMetaTag('name', 'description', description);
    if (keywords) {
      setMetaTag('name', 'keywords', keywords);
    }

    // Canonical
    const canonicalUrl = canonical || `${siteUrl}${location.pathname}`;
    let linkCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.rel = 'canonical';
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.href = canonicalUrl;

    // Robots noindex
    if (noindex) {
      setMetaTag('name', 'robots', 'noindex, nofollow');
    } else {
      const robotsMeta = document.querySelector('meta[name="robots"]');
      if (robotsMeta) robotsMeta.remove();
    }

    // Open Graph
    setMetaTag('property', 'og:title', title);
    setMetaTag('property', 'og:description', description);
    setMetaTag('property', 'og:url', canonicalUrl);
    setMetaTag('property', 'og:type', ogType || 'website');
    setMetaTag('property', 'og:site_name', siteName);
    setMetaTag('property', 'og:locale', 'ko_KR');
    setMetaTag('property', 'og:image', ogImage || defaultOgImage);

    // Twitter Card
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:title', title);
    setMetaTag('name', 'twitter:description', description);
    setMetaTag('name', 'twitter:image', ogImage || defaultOgImage);

    // Last-modified
    setMetaTag('name', 'last-modified', new Date().toISOString().split('T')[0]);

    // Schema.org JSON-LD
    if (schema) {
      const scriptId = 'schema-jsonld';
      let scriptTag = document.getElementById(scriptId);
      if (scriptTag) scriptTag.remove();

      scriptTag = document.createElement('script');
      scriptTag.id = scriptId;
      scriptTag.type = 'application/ld+json';
      scriptTag.textContent = JSON.stringify(schema);
      document.head.appendChild(scriptTag);
    }

    return () => {
      const scriptTag = document.getElementById('schema-jsonld');
      if (scriptTag) scriptTag.remove();
    };
  }, [title, description, keywords, canonical, ogType, ogImage, noindex, schema, location.pathname, siteUrl, siteName, defaultOgImage]);
}
